import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, inject } from '@angular/core';
import {CdkDragDrop, CdkDragEnter, transferArrayItem} from '@angular/cdk/drag-drop';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';

import {Observable, throwError} from 'rxjs';
import {catchError, finalize, tap} from 'rxjs/operators';

import {FeatureFlags, FeatureFlagService} from '@core';

import {HeaderSort, TableComponent, TransportType} from '@shared';

import {
    ActiveEmployeeService, BreakpointObserverService, BreakpointValue,
    DragDropService,
    EmployeeService,
    EmployeeView, NavigationService,
    ProblemInstanceService,
    TaskAssigningIndicator,
    TaskService,
    TaskValidateService,
    TaskView
} from 'planner-shared';
import {SortState} from "../../../../_shared/table/header-sort/models/enums/sort-state.enum";
import {RosterService} from "../../shared/services/roster/roster.service";

@UntilDestroy()
@Component({
    selector: 'app-employee-list',
    templateUrl: './employee-list.component.html',
    styleUrls: ['./employee-list.component.scss'],
    standalone: false
})
export class EmployeeListComponent implements OnInit, OnDestroy {
    private readonly dragDropService = inject(DragDropService);
    private readonly employeeService = inject(EmployeeService);
    private readonly problemInstanceService = inject(ProblemInstanceService);
    private readonly activeEmployeeService = inject(ActiveEmployeeService);
    private readonly featureFlagService = inject(FeatureFlagService);
    private readonly taskService = inject(TaskService);
    private readonly taskValidateService = inject(TaskValidateService);
    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    private readonly navigationService = inject(NavigationService);
    private readonly rosterService = inject(RosterService);
    private readonly breakpointObserverService = inject(BreakpointObserverService);

    @Input() updating: boolean;
    @Output() activeChanged = new EventEmitter<void>();
    @Output() public undraggableStateChanged = new EventEmitter<boolean>();
    public selectAll: boolean;
    public selectRideSharingAll: boolean;
    public loading: boolean;
    public loadingTasks: boolean;
    public draggingTask: boolean;
    public tableHoverEffectDisabled: boolean;
    public showRoster: boolean;
    public taskAssigningIndicator: TaskAssigningIndicator;
    public employees: EmployeeView[];
    public employeesOriginal: EmployeeView[];
    public totalActiveEmployees: number;
    public isRideSharingEnabledOnClient: boolean;
    public isCriticalRosterFeatureEnabled: Observable<boolean>;
    public showEmployeeStatusCol: boolean;
    public breakpointValue: BreakpointValue;
    private readonly InitSortBy = 'hasError';
    private sortValue: HeaderSort;
    private searchPhrase: string;
    @ViewChild(TableComponent, {static: true}) private readonly tableComponent: TableComponent;

    protected readonly BreakpointValue = BreakpointValue;

    constructor() {
        this.employees = new Array();
        this.totalActiveEmployees = 0;
        this.taskAssigningIndicator = <TaskAssigningIndicator>{assigning: false};
    }

    ngOnInit(): void {
        this.isCriticalRosterFeatureEnabled = this.featureFlagService.isEnabled(FeatureFlags.CriticalRosterPriority);

        this.loading = true;
        this.problemInstanceService.rosterDepartmentIsPartOfInstance()
            .pipe(untilDestroyed(this))
            .subscribe(result => this.showRoster = result.valueOf());

        this.tableComponent.headerSortService
            .setHeaderSort(this.InitSortBy, false, null, null, SortState.DESCDENDING_STATE);
        this.tableComponent.headerSortService
            .getHeaderSort()
            .pipe(untilDestroyed(this))
            .subscribe(sortValue => {
                this.sortValue = sortValue;
                this.resetEmployeeList();
                this.loadEmployees();
            });

        this.loadActiveEmployeesCount();

        this.taskService
            .getTaskAssigningIndicator()
            .pipe(untilDestroyed(this))
            .subscribe(indicator => (this.taskAssigningIndicator = indicator));

        this.taskService
            .getGeneralTasksLoadingStatus()
            .pipe(untilDestroyed(this))
            .subscribe(status => (this.loadingTasks = status));

        this.dragDropService
            .draggingTask()
            .pipe(untilDestroyed(this))
            .subscribe(task => {
                this.draggingTask = task !== null;
                if (this.draggingTask) {
                    this.employeesOriginal.forEach(employee => {
                        this.taskService.getEmployeeTasks(employee.workHourId).subscribe(tasks => {
                            const activeEmployee = this.activeEmployeeService.mapToActiveEmployee(employee, tasks);
                            this.taskValidateService
                                .isTaskValidForEmployee(activeEmployee, task)
                                .subscribe(valid => (employee.dropDisabled = !valid));
                        });
                    });
                    this.employees = this.employees.filter(employee => !employee.dropDisabled);
                    this.changeDetectorRef.detectChanges();
                } else {
                    this.employees = this.employeesOriginal;
                    this.changeDetectorRef.markForCheck();
                }
            });
        this.problemInstanceService.getProblemInstance()
            .pipe(untilDestroyed(this))
            .subscribe(problemInstanceResult => {
                this.isRideSharingEnabledOnClient
                    = problemInstanceResult.problemInstance.officeInfo.rideSharingEnabledOnClientSide;
            });

        this.breakpointObserverService.getCurrentBreakpoint()
            .pipe(untilDestroyed(this))
            .subscribe(value => this.breakpointValue = value);
    }

    ngOnDestroy(): void {
        this.deselectEmployee();
    }

    public searchTextChange(searchPhrase: string): void {
        this.searchPhrase = searchPhrase;

        this.deselectEmployee();
        this.resetEmployeeList();
        this.loadEmployees();
    }

    public selectEmployee(employee: EmployeeView): void {
        if (!this.loadingTasks && !employee.selected) {
            this.deselectEmployee();
            employee.selected = true;

            this.employeeService.getEmployees().subscribe(employees => {
                this.navigationService
                    .disableNextButton(
                        employees.some(employee => employee.hasError)
                    );
            });
            this.activeEmployeeService.setActiveEmployee(employee);
        }
    }

    public droppedTask(event: CdkDragDrop<TaskView[]>, employee: EmployeeView): void {
        if (employee.dropDisabled) {
            event.item.data.numberOfEmployees++;
            return;
        }
        this.undraggableStateChanged.emit(true);
        if (!event.item.data.numberOfEmployees) {
            event.previousContainer.data.splice(event.previousIndex, 1);
        }

        this.taskService
            .assignGeneralTaskToEmployee(
                employee.employeeId,
                employee.workHourId,
                event.item.data.originalTaskId,
                event.item.data.employeeId,
                event.item.data.workHourId
            )
            .pipe(
                catchError(error => {
                    if (event.item.data.numberOfEmployees && event.item.data.numberOfEmployees > 0) {
                        event.item.data.numberOfEmployees++;
                    } else {
                        transferArrayItem([event.item.data], event.previousContainer.data, 0, event.previousIndex);
                    }
                    return throwError(error);
                })
            )
            .subscribe(() => {
                if (employee.selected) {
                    this.activeEmployeeService.resetActiveEmployeeTasks();
                }
                this.undraggableStateChanged.emit(false);
            });
    }

    public taskEntered(event: CdkDragEnter<TaskView>, employee: EmployeeView): void {
        this.dragDropService.hidePlaceholder(event);
        if (employee.dropDisabled) {
            this.dragDropService.resetPlaceholderPosition(event);
        }
    }

    public loadActiveEmployeesCount(): void {
        this.employeeService.getActiveEmployeesCount().subscribe(count => (this.totalActiveEmployees = count));
    }

    public loadEmployees(employeeWorkHourIdToSelect?: string): void {
        this.employeeService
            .getEmployees(this.sortValue, this.searchPhrase, false, true)
            .pipe(finalize(() => setTimeout(() => (this.loading = false))))
            .subscribe(employees => {
                this.employeesOriginal = employees;
                this.employees = employees;
                this.checkIfAllSelected();
                this.checkIfAllSelectedRideSharing();
                this.activeEmployeeService.selectActiveEmployee(employees, employeeWorkHourIdToSelect);

                this.rosterService.areEmployeesRosterMissingCase(this.employees).subscribe(missingRosterCase => {
                    this.showEmployeeStatusCol = missingRosterCase.valueOf();
                });
            });
    }

    public toggleActiveSelection(employee: EmployeeView, event: Event): void {
        event.preventDefault();

        employee.isActive = !employee.isActive;
        employee.editing = true;

        if (!employee.isActive && employee.rideSharing) {
            employee.rideSharing = false;
        }

        this.checkIfAllSelectedRideSharing();
        this.checkIfAllSelected();

        this.activeChanged.emit();
    }

    public toggleRideShareSelection(employee: EmployeeView, event: Event): void {
        event.preventDefault();

        employee.rideSharing = !employee.rideSharing;
        employee.editing = true;

        this.checkIfAllSelectedRideSharing();

        this.activeChanged.emit();
    }

    public toggleRideShareSelectionAll(): void {
        this.employees.forEach(employee => {
            if (employee.rideSharing !== this.selectRideSharingAll && (employee.isActive || employee.rideSharing)) {
                employee.rideSharing = this.selectRideSharingAll;
                employee.editing = true;
            }
        });

        this.activeChanged.emit();
    }

    public toggleActiveSelectionAll(): void {
        this.employees.forEach(employee => {
            if (employee.isActive !== this.selectAll) {
                employee.isActive = this.selectAll;
                employee.editing = true;
            }
            if (!this.selectAll) {
                employee.rideSharing = false;
            }
            this.checkIfAllSelectedRideSharing();
        });

        this.activeChanged.emit();
    }

    public checkIfAllSelected(): void {
        const existInactiveEmployee = this.employees.some(employee => !employee.isActive);
        this.selectAll = !existInactiveEmployee;
    }

    public checkIfSomeAreActive(): boolean {
        return this.employees.some(employee => employee.isActive);
    }

    public checkIfAllSelectedRideSharing(): void {
        let activeRideSharing: EmployeeView[];
        activeRideSharing = this.employees.filter(employee => employee.isActive && employee.transport !== TransportType.Bicycle);
        if (activeRideSharing.length === 0) {
            this.selectRideSharingAll = false;
            return;
        }
        this.selectRideSharingAll = activeRideSharing.every(activeEmployee => activeEmployee.rideSharing);
    }

    public isUsingBicycle(employee: EmployeeView): boolean {
        return (employee.transport === TransportType.Bicycle);
    }

    private deselectEmployee(): void {
        this.employeeService
            .getEmployees()
            .pipe(
                tap(employees => {
                    const selectedEmployee = employees?.find(employee => employee.selected);
                    if (selectedEmployee) {
                        selectedEmployee.selected = false;
                    }
                })
            )
            .subscribe();
    }

    private resetEmployeeList(): void {
        this.employees = new Array();
    }
}
